import React, { useState, useEffect, useRef, ReactNode } from 'react';
import c from 'classnames';

import { Button, Icon } from '@repo/ui';
import { useAnalytics } from 'contexts/analytics/AnalyticsProvider';
import LogoSvg from 'assets/images/logos/TH-logo.svg';
import { MobileTapArea } from './components/MobileTapArea';

import styles from './FirstLookCarousel.module.scss';

const SLIDE_DURATION = 6500; // 6.5 seconds per slide

export interface SlideConfig {
  id?: string;
  component: ReactNode;
  backgroundColor: string;
  tapAreaHeight?: string;
}

interface FirstLookCarouselProps {
  slides: SlideConfig[];
  onClose: () => void;
}

export const FirstLookCarousel: React.FC<FirstLookCarouselProps> = ({
  slides,
  onClose,
}) => {
  const { trackEvent } = useAnalytics();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [direction, setDirection] = useState<'forward' | 'backward' | null>(
    null,
  );
  const [lastDirection, setLastDirection] = useState<
    'forward' | 'backward' | null
  >(null);
  const timerRef = useRef<number | null>(null);
  const startTimeRef = useRef<number>(0);
  const remainingTimeRef = useRef<number>(SLIDE_DURATION);
  const pressTimerRef = useRef<number | null>(null);
  const isPressHoldRef = useRef<boolean>(false);

  const closeAndTrackEvent = () => {
    trackEvent('First Look', {
      event_type: 'modal_closed',
      slide_index: currentIndex,
      slide_id: slides[currentIndex]?.id,
      total_slides: slides.length,
    });
    onClose();
  };

  const goToNext = () => {
    if (currentIndex < slides.length - 1) {
      const nextIndex = currentIndex + 1;
      setDirection('forward');
      setIsAnimating(true);

      trackEvent('First Look', {
        event_type: 'slide_advanced',
        from_slide_index: currentIndex,
        from_slide_id: slides[currentIndex]?.id,
        to_slide_index: nextIndex,
        to_slide_id: slides[nextIndex]?.id,
        direction: 'forward',
      });

      setTimeout(() => {
        setCurrentIndex(nextIndex);
        setProgress(0);
        remainingTimeRef.current = SLIDE_DURATION;
        setIsAnimating(false);
        setLastDirection('forward');
        setDirection(null);
        setIsPaused(false);
      }, 300);
    } else {
      // Last slide reached, close modal
      closeAndTrackEvent();
    }
  };

  const goToPrevious = () => {
    if (currentIndex > 0) {
      setDirection('backward');
      setIsAnimating(true);
      const prevIndex = currentIndex - 1;

      trackEvent('First Look', {
        event_type: 'slide_advanced',
        from_slide_index: currentIndex,
        from_slide_id: slides[currentIndex]?.id,
        to_slide_index: prevIndex,
        to_slide_id: slides[prevIndex]?.id,
        direction: 'backward',
      });
      setTimeout(() => {
        setCurrentIndex(prevIndex);
        setProgress(0);
        remainingTimeRef.current = SLIDE_DURATION;
        setIsAnimating(false);
        setLastDirection('backward');
        setDirection(null);
        setIsPaused(false);
      }, 300);
    }
  };

  const handlePressStart = () => {
    isPressHoldRef.current = false;

    // Set a timer - if user holds for 200ms, it's a press-and-hold (pause)
    pressTimerRef.current = setTimeout(() => {
      isPressHoldRef.current = true;
      setIsPaused(true);
    }, 200) as unknown as number;
  };

  const handlePressEnd = (side: 'left' | 'right') => {
    // Clear the press timer
    if (pressTimerRef.current) {
      clearTimeout(pressTimerRef.current);
      pressTimerRef.current = null;
    }

    // If it was a press-and-hold, resume playback
    if (isPressHoldRef.current) {
      setIsPaused(false);
      isPressHoldRef.current = false;
    } else {
      // It was a quick tap - navigate
      if (side === 'left') {
        goToPrevious();
      } else {
        goToNext();
      }
    }
  };

  const togglePause = () => {
    setIsPaused(!isPaused);
  };

  // Auto-advance timer with progress
  useEffect(() => {
    if (isPaused) {
      // Clear timer when paused
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    // Start timer
    startTimeRef.current = Date.now();
    const interval = 50; // Update progress every 50ms for smooth animation
    const startProgress = progress; // Remember where we left off

    timerRef.current = setInterval(() => {
      const elapsed = Date.now() - startTimeRef.current;
      const progressIncrement = (elapsed / remainingTimeRef.current) * 100;
      const newProgress = Math.min(startProgress + progressIncrement, 100);
      setProgress(newProgress);

      if (elapsed >= remainingTimeRef.current) {
        goToNext();
      }
    }, interval) as unknown as number;

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [currentIndex, isPaused]);

  // Update remaining time when pausing/resuming
  useEffect(() => {
    if (isPaused && timerRef.current) {
      const elapsed = Date.now() - startTimeRef.current;
      remainingTimeRef.current = Math.max(
        remainingTimeRef.current - elapsed,
        0,
      );
    }
  }, [isPaused]);

  return (
    <div
      className={styles.carouselWrapper}
      style={{ backgroundColor: slides[currentIndex]?.backgroundColor }}
    >
      <div className={styles.slideIndicators}>
        {slides.map((_, index) => (
          <div key={index} className={styles.indicatorWrapper}>
            <div
              className={c(styles.indicator, {
                [styles.completed]: index < currentIndex,
                [styles.current]: index === currentIndex,
              })}
            />
            {index === currentIndex && (
              <div
                className={styles.indicatorProgress}
                style={{ width: `${progress}%` }}
              />
            )}
          </div>
        ))}
      </div>

      <div className={styles.topControls}>
        <div className={styles.logo}>
          <img src={LogoSvg} alt='Tiny Health' />
        </div>
        <div className={styles.controlButtons}>
          <button
            className={styles.playPauseButton}
            onClick={togglePause}
            aria-label={isPaused ? 'Play' : 'Pause'}
          >
            <Icon name={isPaused ? 'playFilled' : 'pauseFilled'} size={20} />
          </button>
          {/* Mobile close button - inside modal */}
          <button
            className={styles.closeButton}
            onClick={closeAndTrackEvent}
            aria-label='Close'
          >
            <Icon name='closeSm' size={20} />
          </button>
        </div>
      </div>

      <div className={styles.navigationWrapper}>
        <button
          className={c(styles.desktopArrow, styles.leftArrow)}
          onClick={goToPrevious}
          disabled={currentIndex === 0}
          aria-label='Previous slide'
        >
          <Icon name='chevronBack' size={32} />
        </button>

        <div className={styles.slideContainer}>
          <MobileTapArea
            side='left'
            onPressStart={handlePressStart}
            onPressEnd={() => handlePressEnd('left')}
            height={slides[currentIndex]?.tapAreaHeight}
          />

          <div
            key={currentIndex}
            className={c(styles.slideContent, {
              [styles.slideExitForward]: isAnimating && direction === 'forward',
              [styles.slideExitBackward]:
                isAnimating && direction === 'backward',
              [styles.slideEnterFromRight]:
                !isAnimating && lastDirection === 'forward',
              [styles.slideEnterFromLeft]:
                !isAnimating && lastDirection === 'backward',
              [styles.slideEnter]: !isAnimating && !lastDirection,
              [styles.paused]: isPaused,
            })}
          >
            {slides[currentIndex]?.component}
          </div>

          <MobileTapArea
            side='right'
            onPressStart={handlePressStart}
            onPressEnd={() => handlePressEnd('right')}
            height={slides[currentIndex]?.tapAreaHeight}
          />
        </div>

        <button
          className={c(styles.desktopArrow, styles.rightArrow)}
          onClick={goToNext}
          disabled={currentIndex === slides.length - 1}
          aria-label='Next slide'
        >
          <Icon name='chevronForward' size={32} />
        </button>
      </div>
    </div>
  );
};
